import { ErrorCode } from '@biteinc/common';
import {
  CardEntryMethod,
  CardSchemeId,
  CardSchemeIdHelper,
  Gateway,
  IntegrationSystem,
} from '@biteinc/enums';
import { UniversalPaymentHelper } from '@biteinc/helpers-universal';
import type { LocalizableString } from '@biteinc/localization';
import { Strings } from '@biteinc/localization';

import { str } from '~/app/js/localization/localization';

namespace GcnPaymentHelper {
  export const gatewayUsesAuthAndCapture = UniversalPaymentHelper.gatewayUsesAuthAndCapture;

  export function systemSupportsTransactionCancellation(
    system: IntegrationSystem.KioskPayment,
  ): boolean {
    if (!window.hasPaymentTerminal) {
      return true;
    }

    switch (system) {
      case IntegrationSystem.OloPayKioskOrderingApi:
      case IntegrationSystem.OloPayKioskPaymentApi:
      case IntegrationSystem.PinataPay:
      case IntegrationSystem.StripeTerminal:
        return true;
      case IntegrationSystem.Adyen:
      case IntegrationSystem.AurusPay:
      case IntegrationSystem.CardConnect:
      case IntegrationSystem.Chase:
      case IntegrationSystem.Eigen:
      case IntegrationSystem.Elavon:
      case IntegrationSystem.FreedomPayKiosk:
      case IntegrationSystem.Heartland:
      case IntegrationSystem.Moneris:
      case IntegrationSystem.ParPay:
      case IntegrationSystem.PinataPayNative:
      case IntegrationSystem.VerifonePointCloud:
      case IntegrationSystem.VerifonePointSca:
      case IntegrationSystem.Worldpay:
        return false;
    }
  }

  // keep in sync with PaymentHelper.gatewaySupportsPartialRefunds
  export function gatewaySupportsPartialRefunds(gateway: Gateway): boolean {
    switch (gateway) {
      case Gateway.Adyen:
      case Gateway.AtriumLoyalty:
      case Gateway.AtriumStoredValue:
      case Gateway.CardConnect: // If the payment is fully captured
      case Gateway.Chase:
      case Gateway.CreditCall:
      case Gateway.Eigen:
      case Gateway.EigenStoredValue:
      case Gateway.Elavon:
      case Gateway.FreedomPayFlash:
      case Gateway.FreedomPayKiosk:
      case Gateway.Heartland:
      case Gateway.Moneris:
      case Gateway.None:
      case Gateway.ParPay:
      case Gateway.PinataPay:
      case Gateway.PinataPayNative:
      case Gateway.PinataCampusCard:
      case Gateway.PinataStoredValue:
      case Gateway.PinataLoyalty:
      case Gateway.Punchh:
      case Gateway.Square:
      case Gateway.Stripe:
      case Gateway.StripeTerminal:
      case Gateway.VerifonePointCloud:
      case Gateway.Worldpay:
        return true;
      case Gateway.Archer:
      case Gateway.CbordStoredValue:
      case Gateway.QuickchargeCampusCard:
      case Gateway.ChoptLoyalty:
      case Gateway.Como:
      case Gateway.Givex:
      case Gateway.Glory:
      case Gateway.LevelUp:
      case Gateway.LovesRewards:
      case Gateway.OLOGift:
      case Gateway.OloPayKioskOrderingApi:
      case Gateway.OloPayKioskPaymentApi:
      case Gateway.PaytronixGift: // TODO: ??
      case Gateway.PinataCashMachine:
      case Gateway.Spendgo:
      case Gateway.TCC:
      case Gateway.SmoothCommerce:
      case Gateway.Starbucks:
      case Gateway.StarbucksStoredValue:
      case Gateway.Thanx:
      case Gateway.Valutec:
      case Gateway.VerifonePointSca: // TODO(sca): verify
        return false;
    }
  }

  export type TerminalType = 'square' | 'ingenico' | 'verifone' | 'bbpos';
  export function terminalTypeForSystem(system: IntegrationSystem.KioskPayment): TerminalType {
    switch (system) {
      case IntegrationSystem.AurusPay:
      case IntegrationSystem.CardConnect:
      case IntegrationSystem.Chase:
      case IntegrationSystem.Elavon:
      case IntegrationSystem.FreedomPayKiosk:
      case IntegrationSystem.Heartland:
      case IntegrationSystem.Moneris: // TODO: @jerry - actually its PAX.
      case IntegrationSystem.ParPay:
      case IntegrationSystem.PinataPay:
      case IntegrationSystem.PinataPayNative:
      case IntegrationSystem.Worldpay:
        return 'ingenico';
      case IntegrationSystem.VerifonePointCloud:
      case IntegrationSystem.VerifonePointSca:
      case IntegrationSystem.OloPayKioskOrderingApi:
      case IntegrationSystem.OloPayKioskPaymentApi:
      case IntegrationSystem.Eigen: // TODO can also be ingenico
      case IntegrationSystem.Adyen: // Could be more options - but not right now
        return 'verifone';
      case IntegrationSystem.StripeTerminal:
        return 'bbpos';
    }
  }

  export function localizableStringFromCardEntryMethod(
    cardEntryMethod: CardEntryMethod,
  ): LocalizableString {
    switch (cardEntryMethod) {
      case CardEntryMethod.ApplePay:
        return Strings.CARD_ENTRY_APPLE_PAY;
      case CardEntryMethod.CardOnFile:
        return Strings.CARD_ENTRY_CARD_ON_FILE;
      case CardEntryMethod.Chip:
        return Strings.CARD_ENTRY_CHIP;
      case CardEntryMethod.Contactless:
        return Strings.CARD_ENTRY_CONTACTLESS;
      case CardEntryMethod.GooglePay:
        return Strings.CARD_ENTRY_GOOGLE_PAY;
      case CardEntryMethod.ManuallyEntered:
        return Strings.CARD_ENTRY_MANUALLY_ENTERED;
      case CardEntryMethod.ProvidedExternally:
        return Strings.CARD_ENTRY_PROVIDED_EXTERNALLY;
      case CardEntryMethod.Scan:
        return Strings.CARD_ENTRY_SCAN;
      case CardEntryMethod.Swipe:
        return Strings.CARD_ENTRY_SWIPE;
      case CardEntryMethod.Unknown:
        return Strings.CARD_ENTRY_UNKNOWN;
    }
    // @ts-ignore this code could be reached if gcn doesn't yet know about a new CardEntryMethod
    return Strings.CARD_ENTRY_UNKNOWN;
  }

  export function getCardSchemeImage(cardSchemeId: CardSchemeId): string {
    const baseUrl = 'https://assets.getbite.com/images-default/credit-card-icons/';
    switch (cardSchemeId) {
      case CardSchemeId.AmEx:
        return `${baseUrl}card-brand-amex.svg`;
      case CardSchemeId.ChinaUnionPay:
        return `${baseUrl}card-brand-china-union-pay.svg`;
      case CardSchemeId.Diners:
        return `${baseUrl}card-brand-diners-club.svg`;
      case CardSchemeId.Discover:
        return `${baseUrl}card-brand-discover.svg`;
      case CardSchemeId.JCB:
        return `${baseUrl}card-brand-jcb.svg`;
      case CardSchemeId.MasterCard:
        return `${baseUrl}card-brand-master-card.svg`;
      case CardSchemeId.Visa:
        return `${baseUrl}card-brand-visa.svg`;
    }
    return `${baseUrl}card-brand-empty.svg`;
  }

  export function getKioskPaymentErrorMessage(
    errorCode: ErrorCode | undefined,
    entryMode: CardEntryMethod,
    cardSchemeId: CardSchemeId,
  ): string {
    switch (errorCode) {
      case ErrorCode.PaymentTerminalNotFound:
        return str(Strings.ERROR_KIOSK_PAYMENT_DEVICE_NOT_FOUND);
      case ErrorCode.PaymentTerminalOffline:
        return str(Strings.ERROR_KIOSK_PAYMENT_DEVICE_OFFLINE);
      case ErrorCode.PaymentCardSchemeIdNotAccepted:
        return str(Strings.ERROR_CARD_SCHEME_NOT_ACCEPTED).replace(
          '{0}',
          // We should be using a translation function but the values we care about here don't get
          // translated.
          // oxlint-disable-next-line no-restricted-syntax
          CardSchemeIdHelper.name(cardSchemeId),
        );
    }
    if (entryMode === CardEntryMethod.Contactless) {
      return str(Strings.ERROR_KIOSK_CARD_DECLINED_TAP);
    }
    return str(Strings.ERROR_KIOSK_CARD_DECLINED);
  }

  export function canWeRetryKioskPayment(errorCode: ErrorCode | undefined): boolean {
    switch (errorCode) {
      // If we know our payment device is either not found or offline there isn't a point of retrying
      // as the second attempt will fail
      case ErrorCode.PaymentTerminalNotFound:
      case ErrorCode.PaymentTerminalOffline:
        return false;

      default:
        return true;
    }
  }
}

export default GcnPaymentHelper;
